<template>
  <div>
    <v-row no-gutters>
      <v-spacer />
      <load-builder-template @input="templateSetter" />
      <save-builder-template :template-getter="templateGetter" />
    </v-row>
    <v-row class="my-6" no-gutters>
      <v-col data-userflow-id="email-editor" cols="12">
        <email-editor
          ref="emailEditor"
          :appearance="appearance"
          :project-id="projectId"
          :locale="locale"
          :tools="customTools"
          :options="options"
          @load="editorLoaded"
        />
      </v-col>
    </v-row>
  </div>
</template>

<script>
import { EmailEditor } from 'vue-email-editor'
import isEmpty from 'lodash/isEmpty'
import { characterVariables } from '@/mixins/character-variables'
import { getOfferCardCustomJS, extractBestOfferOrder, isEditMode } from './OfferCardConfig'
import { bestOfferValidations } from '@/modules/maria/campanha-da-maria-v2/mixins/best-offer-validations'

export default {
  name: 'BuilderEmailComposer',
  components: {
    EmailEditor,
    LoadBuilderTemplate: () => import('@/modules/campanhas/campanhas-email/views/components/builder-templates/LoadBuilderTemplate.vue'),
    SaveBuilderTemplate: () => import('@/modules/campanhas/campanhas-email/views/components/builder-templates/SaveBuilderTemplate.vue'),
  },
  mixins: [characterVariables, bestOfferValidations],
  props: {
    message: { type: Object, default: () => ({}) },
    campaignData: { type: Object, default: () => ({}) },
    defaultColors: { type: Object, default: () => ({}) },
    subject: { type: String, default: '' },
  },
  data: () => ({
    localBuilderConfiguration: null,
    localProcessedHtml: null,
    productsWithImage: 0,

    offerCardGlobalColors: { mecanicaColor: null, descontoColor: null },
    offerCardGlobalFont: null,

    offerCardKnownIds: new Set(),
    offerCardLastState: new Map(),

    offerCardUpdateTimer: null,
  }),
  computed: {
    appearance() {
      return {
        theme: 'dark',
        panels: { tools: { dock: 'right' } },
      }
    },
    projectId() {
      return 203339
    },
    locale() {
      return 'pt-BR'
    },
    defaultTools() {
      return { image: { enabled: true } }
    },
    bestOfferOptions() {
      const count = Math.min(this.productsWithImage || 0, 10)
      const options = []
      for (let i = 1; i <= count; i++) {
        options.push({ label: `${i}ª Melhor Oferta`, value: i.toString() })
      }
      return options
    },
    customTools() {
      const offerCardProperties = {
        bestOffer: { editor: { data: { options: this.bestOfferOptions } } },
        templateSize: {
          editor: {
            data: {
              options: [
                { label: 'Vertical', value: 'small' },
                { label: 'Horizontal', value: 'large' },
              ],
            },
          },
        },
      }
      return {
        ...this.defaultTools,
        'custom#offer_card': { properties: offerCardProperties },
      }
    },
    bestItemsVariables() {
      if (!this.isMariaCampaign) return []
      const label = `${this.getBestProductText}, do dia {inicio validade} ate {fim validade}]`
      const variableInfo = {
        description: '- Exibe, dos produtos em promoção, qual o consumidor tem mais chance de comprar seguindo os critérios incluso na campanha.',
        charQuantity: this.bestProductCharQuantity,
      }
      return [
        { clipboardText: `[primeiro ${label}`, text: `[primeiro ${label}`, ...variableInfo },
        { clipboardText: `[segundo ${label}`, text: `[segundo ${label}`, ...variableInfo },
        { clipboardText: `[terceiro ${label}`, text: `[terceiro ${label}`, ...variableInfo },
      ]
    },
    builderEmailVariables() {
      const excludedVariables = [
        '{fim validade}',
        '{inicio validade}',
        '{nome do combo}',
        '{nome produto}',
        '{preço 1}',
        '{preço 2}',
        '{desconto}',
        '{leve}',
        '{pague}',
        'primeiro melhor produto ',
        'segundo melhor produto ',
        'terceiro melhor produto ',
        'primeiro melhor combo ',
        'segundo melhor combo ',
        'terceiro melhor combo ',
        'Melhor produto',
        'Melhor combo',
      ]
      return this.variables
        .filter((item) => !excludedVariables.includes(item.clipboardText) && !['Melhor produto', 'Melhor combo'].includes(item.text))
        .concat(this.bestItemsVariables)
    },
    options() {
      return {
        translations: {
          'pt-BR': {
            'labels.merge_tags': 'Variáveis',
            missing_component_placeholder_text: 'Este componente não está disponível',
          },
          'en-US': {
            'labels.merge_tags': 'Variables',
            missing_component_placeholder_text: 'This component is not available',
          },
        },
        mergeTags: this.builderEmailVariables.reduce((acc, variable) => {
          return {
            [variable.clipboardText]: {
              value: variable.clipboardText,
              name: variable.text,
              sample: variable.example,
            },
            ...acc,
          }
        }, {}),
        ...(this.productsWithImage > 0 && {
          customJS: [getOfferCardCustomJS(this.defaultColors, Math.min(this.productsWithImage || 0, 10), this.message)],
        }),
      }
    },
  },
  created() {
    if (!isEmpty(this.message?.builder_email_configuration)) {
      this.localBuilderConfiguration = this.message?.builder_email_configuration
    }
    this.productsWithImage = this.validateAllProductsImages()
  },
  beforeUnmount() {
    if (this.offerCardUpdateTimer) {
      clearTimeout(this.offerCardUpdateTimer)
      this.offerCardUpdateTimer = null
    }
  },
  methods: {
    ensureGlobalState() {
      if (!this.offerCardGlobalColors || typeof this.offerCardGlobalColors !== 'object') {
        this.offerCardGlobalColors = { mecanicaColor: null, descontoColor: null }
      }
      if (typeof this.offerCardGlobalFont === 'undefined') {
        this.offerCardGlobalFont = null
      }
      if (!this.offerCardKnownIds || !(this.offerCardKnownIds instanceof Set)) {
        this.offerCardKnownIds = new Set()
      }
      if (!this.offerCardLastState || !(this.offerCardLastState instanceof Map)) {
        this.offerCardLastState = new Map()
      }
    },

    setGlobalColorsInDesign(design, colors) {
      if (!design) return
      design.body = design.body || {}
      design.body.values = design.body.values || {}
      design.body.values._meta = design.body.values._meta || {}
      const HEX = /^#([0-9a-f]{3}|[0-9a-f]{6})$/i
      const mec = colors?.mecanicaColor && HEX.test(colors.mecanicaColor) ? colors.mecanicaColor : null
      const des = colors?.descontoColor && HEX.test(colors.descontoColor) ? colors.descontoColor : null
      design.body.values._meta.offerCardGlobalColors = { mecanicaColor: mec, descontoColor: des }
    },
    getGlobalColorsFromDesign(design) {
      return design?.body?.values?._meta?.offerCardGlobalColors || null
    },
    setGlobalFontInDesign(design, font) {
      if (!design) return
      design.body = design.body || {}
      design.body.values = design.body.values || {}
      design.body.values._meta = design.body.values._meta || {}
      const ok = font && typeof font === 'object' && typeof font.value === 'string'
      design.body.values._meta.offerCardGlobalFont = ok ? { label: font.label || 'Custom', value: font.value } : null
    },
    getGlobalFontFromDesign(design) {
      return design?.body?.values?._meta?.offerCardGlobalFont || null
    },
    scanOfferCards(node, acc = [], path = 'body') {
      if (!node || typeof node !== 'object') return acc
      const isOfferCard = (content) => {
        if (!content) return false
        if (content.type === 'custom' && content.slug === 'offer_card') return true
        const htmlClass = content.values?._meta?.htmlClassNames || ''
        const htmlID = content.values?._meta?.htmlID || ''
        if (typeof htmlClass === 'string' && htmlClass.includes('u_content_custom_offer_card')) return true
        if (typeof htmlID === 'string' && htmlID.includes('u_content_custom_offer_card')) return true
        if (content.values?._meta?.customToolName === 'offer_card') return true
        return false
      }
      if ('type' in node || 'slug' in node || 'values' in node) {
        if (isOfferCard(node)) acc.push(node)
      }
      const dive = (child, label) => this.scanOfferCards(child, acc, `${path}.${label}`)
      if (Array.isArray(node.rows)) node.rows.forEach((r, i) => dive(r, `rows[${i}]`))
      if (Array.isArray(node.columns)) node.columns.forEach((c, i) => dive(c, `columns[${i}]`))
      if (Array.isArray(node.contents)) node.contents.forEach((c, i) => dive(c, `contents[${i}]`))
      Object.keys(node).forEach((k) => {
        const v = node[k]
        if (v && typeof v === 'object' && (v.rows || v.columns || v.contents)) dive(v, k)
      })
      return acc
    },
    extractOfferState(c) {
      const v = (c && c.values) || {}
      return {
        mecanica: v.mecanicaOfferColor || null,
        desconto: v.descontoColor || null,
        font: v.fontFamily && typeof v.fontFamily === 'object' ? v.fontFamily.value : null,
      }
    },
    stateToKey(s) {
      return `${s.mecanica || ''}|${s.desconto || ''}|${s.font || ''}`
    },
    isOfferCardContent(content) {
      if (!content) return false
      if (content.type === 'custom' && content.slug === 'offer_card') return true

      const v = content.values || {}
      const htmlClass = v._meta?.htmlClassNames || ''
      const htmlID = v._meta?.htmlID || ''

      if (typeof htmlClass === 'string' && htmlClass.includes('u_content_custom_offer_card')) return true
      if (typeof htmlID === 'string' && htmlID.includes('u_content_custom_offer_card')) return true
      if (v._meta?.customToolName === 'offer_card') return true

      return false
    },
    createWarningTextContent(originalContent, message) {
      const padding = originalContent?.values?.containerPadding || '16px'
      return {
        type: 'text',
        values: {
          _meta: {
            ...(originalContent?.values?._meta || {}),
            replacedFrom: 'offer_card',
          },
          containerPadding: padding,
          text: `<div id="not_allowed" style="border:2px dashed red;color:red;padding:16px;text-align:center;font-family:sans-serif;font-size:14px;">
            ${message || 'A configuração atual da oferta não permite o uso deste componente.'}
          </div>`,
        },
      }
    },
    replaceOfferCardsWithWarning(design, message) {
      if (!design || !design.body) return design

      const clone = JSON.parse(JSON.stringify(design))

      const walk = (node) => {
        if (!node || typeof node !== 'object') return node

        if (Array.isArray(node.contents)) {
          node.contents = node.contents.map((c) => {
            if (this.isOfferCardContent(c)) {
              return this.createWarningTextContent(c, message)
            }
            return walk(c)
          })
        }

        if (Array.isArray(node.rows)) node.rows = node.rows.map((r) => walk(r))
        if (Array.isArray(node.columns)) node.columns = node.columns.map((c) => walk(c))

        Object.keys(node).forEach((k) => {
          const v = node[k]
          if (v && typeof v === 'object' && (v.rows || v.columns || v.contents)) {
            node[k] = walk(v)
          }
        })

        return node
      }

      clone.body = walk(clone.body)

      if (clone.counters && typeof clone.counters === 'object') {
        clone.counters.u_content_custom_offer_card = 0
      }

      return clone
    },
    configureOfferCardsWithSavedOrder() {
      // Configurar cards com a ordem salva se estiver em modo de edição
      if (isEditMode(this.message)) {
        const bestOfferOrder = extractBestOfferOrder(this.message?.builder_email_configuration)
        if (bestOfferOrder && bestOfferOrder.length > 0) {
          // Aguardar um pouco para o DOM estar pronto
          setTimeout(() => {
            bestOfferOrder.forEach((order, index) => {
              if (order.htmlID && window.configureOfferCard) {
                // Configurar cada card com sua ordem salva
                window.configureOfferCard(order.htmlID, order.bestOffer)
              }
            })
          }, 1000)
        }
      }
    },

    editorLoaded() {
      const editor = this.$refs.emailEditor?.editor
      if (!editor) return

      this.ensureGlobalState()
      window.maxOfferCards = this.productsWithImage

      editor.addEventListener('design:loaded', () => {
        editor.saveDesign((design) => {
          this.ensureGlobalState()

          // Restaurar globais do meta
          const savedColors = this.getGlobalColorsFromDesign(design)
          if (savedColors) {
            if (savedColors.mecanicaColor) this.offerCardGlobalColors.mecanicaColor = savedColors.mecanicaColor
            if (savedColors.descontoColor) this.offerCardGlobalColors.descontoColor = savedColors.descontoColor
          }
          const savedFont = this.getGlobalFontFromDesign(design)
          if (savedFont && typeof savedFont.value === 'string') {
            this.offerCardGlobalFont = savedFont
          }

          const found = this.scanOfferCards(design?.body)
          const first = found?.[0]
          if (first?.values) {
            if (!savedColors) {
              this.offerCardGlobalColors.mecanicaColor = first.values.mecanicaOfferColor ?? this.offerCardGlobalColors.mecanicaColor
              this.offerCardGlobalColors.descontoColor = first.values.descontoColor ?? this.offerCardGlobalColors.descontoColor
            }
            if (!savedFont && first.values.fontFamily?.value) {
              this.offerCardGlobalFont = first.values.fontFamily
            }
          }

          this.offerCardKnownIds = new Set()
          this.offerCardLastState = new Map()
          const all = this.scanOfferCards(design?.body)
          all.forEach((c) => {
            const id = c.values?._meta?.htmlID || c.id || c.values?._meta?.htmlClassNames
            if (!id) return
            this.offerCardKnownIds.add(id)
            this.offerCardLastState.set(id, this.stateToKey(this.extractOfferState(c)))
          })
        })
      })

      editor.addEventListener('design:updated', () => {
        clearTimeout(this.offerCardUpdateTimer)
        this.offerCardUpdateTimer = setTimeout(() => {
          editor.saveDesign((design) => {
            if (!design?.body) return
            this.ensureGlobalState()

            const cards = this.scanOfferCards(design.body)
            let changedByUser = false
            let changed = false
            const newIds = []

            for (const c of cards) {
              const id = c.values?._meta?.htmlID || c.id || c.values?._meta?.htmlClassNames
              if (!id) continue
              c.values = c.values && typeof c.values === 'object' ? c.values : {}

              const cur = this.extractOfferState(c)
              const curKey = this.stateToKey(cur)
              const prevKey = this.offerCardLastState.get(id)

              if (!this.offerCardKnownIds.has(id)) {
                newIds.push(id)
              }

              if (prevKey && prevKey !== curKey) {
                if (cur.mecanica) this.offerCardGlobalColors.mecanicaColor = cur.mecanica
                if (cur.desconto) this.offerCardGlobalColors.descontoColor = cur.desconto
                if (cur.font) this.offerCardGlobalFont = { label: 'Custom', value: cur.font }
                changedByUser = true
              }
            }

            const mec = this.offerCardGlobalColors?.mecanicaColor || null
            const des = this.offerCardGlobalColors?.descontoColor || null
            const fnt = this.offerCardGlobalFont && typeof this.offerCardGlobalFont.value === 'string' ? this.offerCardGlobalFont : null

            if (changedByUser) {
              cards.forEach((c) => {
                const before = JSON.stringify(c.values || {})
                c.values = c.values && typeof c.values === 'object' ? c.values : {}
                if (mec) c.values.mecanicaOfferColor = mec
                if (des) c.values.descontoColor = des
                if (fnt) c.values.fontFamily = fnt
                if (JSON.stringify(c.values) !== before) changed = true
              })
            }

            if (!changedByUser && (mec || des || fnt) && newIds.length) {
              cards.forEach((c) => {
                const id = c.values?._meta?.htmlID || c.id || c.values?._meta?.htmlClassNames
                if (!id || !newIds.includes(id)) return
                const before = JSON.stringify(c.values || {})
                c.values = c.values && typeof c.values === 'object' ? c.values : {}
                if (mec) c.values.mecanicaOfferColor = mec
                if (des) c.values.descontoColor = des
                if (fnt) c.values.fontFamily = fnt
                if (JSON.stringify(c.values) !== before) changed = true
              })
            }

            if (changed) {
              this.setGlobalColorsInDesign(design, this.offerCardGlobalColors)
              this.setGlobalFontInDesign(design, this.offerCardGlobalFont)
              editor.loadDesign(design)
            }

            for (const c of cards) {
              const id = c.values?._meta?.htmlID || c.id || c.values?._meta?.htmlClassNames
              if (!id) continue
              this.offerCardKnownIds.add(id)
              this.offerCardLastState.set(id, this.stateToKey(this.extractOfferState(c)))
            }
          })
        }, 250)
      })

      if (this.localBuilderConfiguration) {
        const cfg =
          (this.productsWithImage || 0) === 0
            ? this.replaceOfferCardsWithWarning({ ...this.localBuilderConfiguration }, 'A configuração atual da oferta não permite o uso deste componente.')
            : this.localBuilderConfiguration
        editor.loadDesign(cfg)

        // Configurar cards com a ordem salva após carregar o design
        this.configureOfferCardsWithSavedOrder()
      }
    },

    async exportDesign() {
      return new Promise((resolve) => this.$refs.emailEditor.editor.saveDesign(resolve))
    },
    async exportHtml() {
      return new Promise((resolve, reject) => {
        try {
          this.$refs.emailEditor.editor.exportHtml((data) => resolve(data.html))
        } catch (error) {
          reject(error)
        }
      })
    },
    async getConfiguration() {
      const processedHtml = await this.exportHtml()
      const builderEmailConfiguration = await this.exportDesign()
      const amountBestOffer = this.calculateAmountBestOffer(processedHtml)
      return {
        processed_html: processedHtml,
        builder_email_configuration: builderEmailConfiguration,
        amount_best_offers: amountBestOffer,
      }
    },
    calculateAmountBestOffer(processedHtml = null) {
      if (!processedHtml) {
        return null
      }

      const hasOfferCardClasses = processedHtml.includes('offer-card-small-container') || processedHtml.includes('offer-card-container')
      if (!hasOfferCardClasses) {
        return null
      }

      return Math.min(this.productsWithImage || 0, 10)
    },
    async templateGetter() {
      const configuration = await this.exportDesign()
      return configuration
    },
    templateSetter(emailTemplate) {
      this.offerCardKnownIds = new Set()
      this.offerCardLastState = new Map()
      let cfg = emailTemplate.configuration

      if ((this.productsWithImage || 0) === 0) {
        cfg = this.replaceOfferCardsWithWarning(cfg, 'A configuração atual da oferta não permite o uso do componente "Melhor oferta" no corpo do e-mail.')
      }
      this.$refs.emailEditor.editor.loadDesign(cfg)
    },
    async validInput() {
      const design = await this.exportDesign()
      if (!design || Object.keys(design).length === 0) return false

      const hasOfferCardInDesign = design.counters && design.counters.u_content_custom_offer_card > 0
      if (hasOfferCardInDesign) {
        const productsWithImage = this.validateAllProductsImages()
        if (productsWithImage === 0) {
          this.$snackbar({
            message: 'A configuração atual da oferta não permite o uso do componente "Melhor oferta" no corpo do e-mail.',
            snackbarColor: 'error',
          })
          return false
        }
      }
      const hasNotAllowedElement = await this.checkForNotAllowedElementInHTML()
      if (hasNotAllowedElement) {
        this.$snackbar({
          message: 'A configuração atual da oferta não permite o uso do componente "Melhor oferta" no corpo do e-mail.',
          snackbarColor: 'error',
        })
        return false
      }
      return true
    },
    isTouched() {
      return true
    },
    async checkForNotAllowedElementInHTML() {
      try {
        const htmlContent = await this.exportHtml()
        if (!htmlContent) {
          return false
        }
        const hasNotAllowed = htmlContent.includes('id="not_allowed"')
        return hasNotAllowed
      } catch (error) {
        console.error('Erro ao verificar HTML para elementos não permitidos:', error)
        return false
      }
    },
  },
}
</script>

<style lang="scss">
.unlayer-editor {
  height: 1000px;
}
</style>
